import { useDispatch, useSelector } from "react-redux";
import { Link, useParams, useSearchParams } from "react-router-dom";
import NavigationTop from "../../components/NavigationTop/NavigationTop";
import { useEffect, useState } from "react";
import { fetchDetailTutorials, clearDetailTutorial } from "../../store/slices/detailTutorialSlice";
import styles from "./Tutorials.module.css";
import tpStyles from "../../components/TutorialPreview/TutorialPreview.module.css";
import Slider from "../../components/Slider/Slider";
import { fetchTutorials } from "../../store/slices/tutorialsSlice";
import { Helmet } from "react-helmet-async";
import VideoPlayer from "../../components/VideoPlayer/VideoPlayer";
import "./DetailTutorialV2.css";
import { resolveMediaUrl } from "../../utils/mediaUrl";

const DetailTutorialV2 = () => {
  const [visibleSlides, setVisibleSlides] = useState(3);
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const previewToken = searchParams.get('preview');
  const dispatch = useDispatch();
  const { detailTutorials, status, error } = useSelector(
    (state) => state.detailTutorial
  );
  const metaTitle = detailTutorials
    ? `${detailTutorials.title} – Distortion Grid Tutorial`
    : "Tutorial – Distortion Grid Guide";
  const metaDescription =
    detailTutorials?.short_description ||
    "Deep dive into distortion grid tutorials from Matchmove machine.";
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const updateVisibleSlides = () => {
    if (window.innerWidth < 690) {
      setVisibleSlides(1);
    } else if (window.innerWidth < 1268) {
      setVisibleSlides(2);
    } else {
      setVisibleSlides(3);
    }
  };

  useEffect(() => {
    updateVisibleSlides();
    window.addEventListener('resize', updateVisibleSlides);
    return () => {
      window.removeEventListener('resize', updateVisibleSlides);
    };
  }, []);

  const { tutorials, status: tutorialsStatus } = useSelector((state) => state.tutorials);

  useEffect(() => {
    if (slug) {
      dispatch(clearDetailTutorial());
      dispatch(fetchDetailTutorials({ slug, previewToken }));
    }
  }, [dispatch, slug, previewToken]);

  useEffect(() => {
    dispatch(fetchTutorials({ page: 1, page_size: 100 }));
  }, [dispatch]);

  const items = tutorials?.data
    ?.filter((item) => item.slug !== slug)
    ?.map((item, index) => (
      <article
        key={item.id}
        className={tpStyles.tutorial}
        onMouseEnter={() => setHoveredIndex(index)}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <Link to={`/tutorials/${item.slug}`} className={tpStyles.tutorialLink}>
          <div className={`${tpStyles.hoveredImage} ${hoveredIndex === index ? tpStyles.visible : tpStyles.hidden}`}>
            <div className={tpStyles.readMore}>
              Read More
              <svg
                width="27"
                height="36"
                viewBox="0 0 27 36"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M16.707 11.9908L22.364 17.6478C22.5515 17.8354 22.6568 18.0897 22.6568 18.3548C22.6568 18.62 22.6568 18.8743 22.364 19.0618L16.707 24.7188C16.5184 24.901 16.2658 25.0018 16.0036 24.9995C15.7414 24.9972 15.4906 24.8921 15.3052 24.7067C15.1198 24.5212 15.0146 24.2704 15.0123 24.0082C15.01 23.746 15.1108 23.4934 15.293 23.3048L19.243 19.3548L6 19.3548C5.73478 19.3548 5.48043 19.2495 5.29289 19.0619C5.10536 18.8744 5 18.6201 5 18.3548C5 18.0896 5.10536 17.8353 5.29289 17.6477C5.48043 17.4602 5.73478 17.3548 6 17.3548L19.243 17.3548L15.293 13.4048C15.1975 13.3126 15.1213 13.2022 15.0689 13.0802C15.0165 12.9582 14.9889 12.827 14.9877 12.6942C14.9866 12.5615 15.0119 12.4298 15.0622 12.3069C15.1125 12.184 15.1867 12.0723 15.2806 11.9784C15.3745 11.8845 15.4861 11.8103 15.609 11.76C15.7319 11.7097 15.8636 11.6844 15.9964 11.6856C16.1292 11.6867 16.2604 11.7143 16.3824 11.7667C16.5044 11.8191 16.6148 11.8953 16.707 11.9908Z" />
              </svg>
            </div>
          </div>
          <div className={tpStyles.tutorialImg}>
            <img src={resolveMediaUrl(item.cover)} alt={item.title} />
          </div>
        </Link>
        <h3 className={`h3-medium ${tpStyles.tutorialTitle}`}>{item.title}</h3>
        <p className={`h5-light ${tpStyles.tutorialText}`}>{item.short_description}</p>
      </article>
    ));

  if (status === "loading" && !detailTutorials) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh',
        fontSize: '18px',
        color: '#666'
      }}>
        <Helmet>
          <title>{metaTitle}</title>
          <meta name="description" content={metaDescription} />
        </Helmet>
        Loading tutorial...
      </div>
    );
  }

  if (status === "failed" && !detailTutorials) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh',
        fontSize: '18px',
        color: '#e74c3c',
        gap: '20px'
      }}>
        <Helmet>
          <title>{metaTitle}</title>
          <meta name="description" content={metaDescription} />
        </Helmet>
        <div>Failed to load tutorial. Please try again.</div>
        <button
          onClick={() => dispatch(fetchDetailTutorials({ slug, previewToken }))}
          style={{
            padding: '10px 20px',
            backgroundColor: '#2f57bf',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!detailTutorials) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh',
        fontSize: '18px',
        color: '#666'
      }}>
        <Helmet>
          <title>{metaTitle}</title>
          <meta name="description" content={metaDescription} />
        </Helmet>
        Tutorial not found.
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
      </Helmet>
      <div className={styles.titleBlock}>
        {detailTutorials && (
          <NavigationTop
            title={detailTutorials.title}
            singleTutorial={detailTutorials.title}
          />
        )}
      </div>
      <div className={styles.blockDetail}>
        {detailTutorials.status === 'draft' && (
          <div className="draft-banner">
            Предпросмотр (черновик) — эта страница не опубликована
          </div>
        )}
        {status === "loading" && !detailTutorials?.blocks ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '40px',
            fontSize: '16px',
            color: '#666'
          }}>
            Loading tutorial content...
          </div>
        ) : (
          detailTutorials?.blocks?.map((block, index) => (
            <div key={block.id}>
              {block.type === "image" ? (
                <img
                  className="tutorial-image-block"
                  src={resolveMediaUrl(block.image)}
                  alt={block.alt_text}
                />
              ) : block.type === "video" ? (
                <div className="tutorial-video-block">
                  {block.video_url ? (
                    <VideoPlayer
                      src={block.video_url}
                      title={block.title || "Tutorial video"}
                      videoTitle={block.title}
                      thumbnail={resolveMediaUrl(block.thumbnail)}
                    />
                  ) : null}
                </div>
              ) : (
                <div
                  className="tutorial-content"
                  dangerouslySetInnerHTML={{ __html: block.text || '' }}
                />
              )}
            </div>
          ))
        )}
      </div>
      <div id="other-slides" className={styles.otherTutorials}>
        <h2 className={`h2-medium ${styles.titleOtherTutorials}`} style={{ color: "white", fontWeight: "450" }}>Other tutorials</h2>
        {tutorialsStatus === "loading" ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '40px',
            fontSize: '16px',
            color: '#666'
          }}>
            Loading other tutorials...
          </div>
        ) : tutorialsStatus === "failed" ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '40px',
            fontSize: '16px',
            color: '#e74c3c',
            gap: '20px'
          }}>
            <div>Failed to load other tutorials</div>
            <button
              onClick={() => dispatch(fetchTutorials({ page: 1, page_size: 100 }))}
              style={{
                padding: '8px 16px',
                backgroundColor: '#2f57bf',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Retry
            </button>
          </div>
        ) : tutorials?.data && items?.length > 0 ? (
          <Slider items={items} visibleSlides={visibleSlides} />
        ) : (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '40px',
            fontSize: '16px',
            color: '#666'
          }}>
            No other tutorials available
          </div>
        )}
      </div>
    </div>
  );
};

export default DetailTutorialV2;
